"use client";

import React, { useState, useRef } from 'react';
import { CodeBlock } from './code-block';

export function SupernovaColorPalette() {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold mb-4">Supernova Colors</h2>
        <p className="text-muted-foreground mb-6">
          These colors are inspired by real supernova images, capturing the vibrant cosmic palette of these stellar explosions.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <ColorSwatch name="Blue" color="blue" description="Bright blue from Crab Nebula" />
          <ColorSwatch name="Teal" color="teal" description="Teal from supernova remnants" />
          <ColorSwatch name="Cyan" color="cyan" description="Cyan from ionized oxygen" />
          <ColorSwatch name="Purple" color="purple" description="Purple from galaxy rings" />
          <ColorSwatch name="Magenta" color="magenta" description="Magenta from hot gas" />
          <ColorSwatch name="Pink" color="pink" description="Pink from hydrogen emissions" />
          <ColorSwatch name="Red" color="red" description="Red from supernova core" />
          <ColorSwatch name="Orange" color="orange" description="Orange from explosion heat" />
          <ColorSwatch name="Yellow" color="yellow" description="Yellow from star core" />
          <ColorSwatch name="Green" color="green" description="Green from nebula gases" />
          <ColorSwatch name="Gold" color="gold" description="Gold from star burst" />
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold mb-4">Supernova Gradients</h2>
        <p className="text-muted-foreground mb-6">
          These gradients are inspired by different types of supernovas and their remnants, capturing the spectacular cosmic phenomena.
        </p>
        
        <h3 className="text-xl font-semibold mb-3">Horizontal Gradients</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <GradientSwatch name="Crab Nebula" gradient="crab" description="Blue → Teal → Green" />
          <GradientSwatch name="Galaxy Ring" gradient="galaxy" description="Purple → Magenta → Pink" />
          <GradientSwatch name="Star Burst" gradient="burst" description="Red → Orange → Yellow" />
          <GradientSwatch name="Nebula Remnant" gradient="remnant" description="Blue → Purple → Red" />
        </div>
        
        <h3 className="text-xl font-semibold mb-3">Vertical Gradients</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <GradientSwatch name="Crab Nebula" gradient="crab-vertical" vertical={true} description="Blue → Teal → Green" />
          <GradientSwatch name="Galaxy Ring" gradient="galaxy-vertical" vertical={true} description="Purple → Magenta → Pink" />
          <GradientSwatch name="Star Burst" gradient="burst-vertical" vertical={true} description="Red → Orange → Yellow" />
          <GradientSwatch name="Nebula Remnant" gradient="remnant-vertical" vertical={true} description="Blue → Purple → Red" />
        </div>
      </div>
      
      <div>
        <h2 className="text-2xl font-bold mb-4">Usage</h2>
        <p className="text-muted-foreground mb-6">
          Use these colors and gradients to create cosmic-inspired UI elements throughout your application.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Text Colors</h3>
            <div className="p-4 bg-white dark:bg-gray-800 rounded-md shadow-sm">
              <p className="text-supernova-blue font-medium mb-2">text-supernova-blue</p>
              <p className="text-supernova-purple font-medium mb-2">text-supernova-purple</p>
              <p className="text-supernova-pink font-medium mb-2">text-supernova-pink</p>
              <p className="text-supernova-red font-medium mb-2">text-supernova-red</p>
              <p className="text-supernova-orange font-medium mb-2">text-supernova-orange</p>
              <p className="text-supernova-teal font-medium">text-supernova-teal</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Background Colors</h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 bg-supernova-blue/10 rounded-md">bg-supernova-blue/10</div>
              <div className="p-3 bg-supernova-purple/10 rounded-md">bg-supernova-purple/10</div>
              <div className="p-3 bg-supernova-pink/10 rounded-md">bg-supernova-pink/10</div>
              <div className="p-3 bg-supernova-red/10 rounded-md">bg-supernova-red/10</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorSwatch({ name, color, description }: { name: string; color: string; description: string }) {
  // Map of color names to their corresponding Tailwind classes
  const colorClasses: Record<string, string> = {
    blue: "bg-supernova-blue",
    teal: "bg-supernova-teal",
    cyan: "bg-supernova-cyan",
    purple: "bg-supernova-purple",
    magenta: "bg-supernova-magenta",
    pink: "bg-supernova-pink",
    red: "bg-supernova-red",
    orange: "bg-supernova-orange",
    yellow: "bg-supernova-yellow",
    green: "bg-supernova-green",
    gold: "bg-supernova-gold"
  };

  return (
    <div className="flex flex-col">
      <div className={`h-24 rounded-md ${colorClasses[color]} mb-2`}></div>
      <div className="text-sm font-medium">{name}</div>
      <div className="text-xs text-muted-foreground">{description}</div>
      <code className="text-xs mt-1">text-supernova-{color}</code>
    </div>
  );
}

function GradientSwatch({ name, gradient, description, vertical = false }: { name: string; gradient: string; description: string; vertical?: boolean }) {
  // Map of gradient names to their corresponding Tailwind classes
  const gradientClasses: Record<string, string> = {
    crab: "bg-supernova-crab",
    galaxy: "bg-supernova-galaxy",
    burst: "bg-supernova-burst",
    remnant: "bg-supernova-remnant",
    "crab-vertical": "bg-supernova-crab-vertical",
    "galaxy-vertical": "bg-supernova-galaxy-vertical",
    "burst-vertical": "bg-supernova-burst-vertical",
    "remnant-vertical": "bg-supernova-remnant-vertical",
    // Legacy names
    cosmic: "bg-supernova-cosmic",
    sunset: "bg-supernova-sunset",
    aurora: "bg-supernova-aurora",
    ocean: "bg-supernova-ocean",
    "cosmic-vertical": "bg-supernova-cosmic-vertical",
    "sunset-vertical": "bg-supernova-sunset-vertical",
    "aurora-vertical": "bg-supernova-aurora-vertical",
    "ocean-vertical": "bg-supernova-ocean-vertical"
  };

  return (
    <div className={`p-6 rounded-lg ${gradientClasses[gradient]} ${vertical ? 'h-64' : ''} flex flex-col justify-between`}>
      <h3 className="text-white text-lg font-bold">{name}</h3>
      <div>
        <p className="text-white/80 text-sm mb-2">{description}</p>
        <code className="text-xs bg-black/20 px-2 py-1 rounded text-white">bg-supernova-{gradient}</code>
      </div>
    </div>
  );
}

export function SupernovaCodeBlock({ children }: { children: React.ReactNode }) {
  return (
    <CodeBlock theme="supernova" className="not-prose">
      {children}
    </CodeBlock>
  );
}

export function SupernovaImageCard({ 
  src, 
  alt, 
  title, 
  description 
}: { 
  src: string; 
  alt: string; 
  title: string; 
  description: string;
}) {
  return (
    <div className="rounded-lg overflow-hidden shadow-md bg-white dark:bg-gray-800">
      <div className="relative h-48 overflow-hidden">
        <img 
          src={src} 
          alt={alt} 
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <h3 className="absolute bottom-3 left-4 text-white text-lg font-bold">{title}</h3>
      </div>
      <div className="p-4">
        <p className="text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

export function SupernovaGradientButton({ 
  children, 
  gradient = "crab", 
  onClick 
}: { 
  children: React.ReactNode; 
  gradient?: string; 
  onClick?: () => void;
}) {
  // Map of gradient names to their corresponding Tailwind classes
  const gradientClasses: Record<string, string> = {
    crab: "bg-supernova-crab",
    galaxy: "bg-supernova-galaxy",
    burst: "bg-supernova-burst",
    remnant: "bg-supernova-remnant",
    cosmic: "bg-supernova-cosmic",
    sunset: "bg-supernova-sunset",
    aurora: "bg-supernova-aurora",
    ocean: "bg-supernova-ocean"
  };

  return (
    <button 
      onClick={onClick}
      className={`px-6 py-3 rounded-md ${gradientClasses[gradient] || "bg-supernova-crab"} text-white font-medium shadow-md hover:shadow-lg transition-shadow`}
    >
      {children}
    </button>
  );
} 